import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CustomerReviews } from './CustomerReviews';
import { FormatAccordion } from './FormatAccordion';
import { JobMonitor } from './JobMonitor';
import { MomentsSection } from './MomentsSection';
import { PreviewImage } from './PreviewImage';
import { ProgressBar } from './ProgressBar';
import { RetryImage } from './RetryImage';
import { RetryPanel, MAX_RETRIES } from './RetryPanel';
import { StepBar } from './StepBar';
import { SupportFaq } from './SupportFaq';
import { useGenerateFlow } from '../hooks/useGenerateFlow';
import { useWakeLock } from '../hooks/useWakeLock';
import { ApiError, getPreview, reapplyOverlay, type PreviewApiResponse } from '../services/api';
import { CartError, addToCart, goToCheckout } from '../services/shopify-cart';
import type { InputSetCode, Look, PreviewMountData, ShopifyVariant, StyleSample, Version } from '../types';
import { getWatermarkUrl } from '../utils/watermark';
import { getRecentPortraits, saveRecentPortrait } from '../utils/recentPortraits';
import { shopifyImg } from '../utils/img';
import { getInputConfig } from '../utils/inputConfigs';

interface Props {
  data: PreviewMountData;
}

type CheckoutPhase = 'idle' | 'submitting' | 'redirecting';

function getJobIdFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('id');
}

/** Split a merged product's "{lookId}__{comboId}" style_id. Null if it isn't one. */
function splitLookStyleId(styleId: string): { lookId: string; comboId: string } | null {
  const sep = styleId.indexOf('__');
  if (sep === -1) return null;
  return { lookId: styleId.slice(0, sep), comboId: styleId.slice(sep + 2) };
}

/**
 * Resolve a style_id (plain, or a merged product's "{lookId}__{comboId}"
 * compound — see types.ts Look doc-comment) to its display sample. Looked up
 * per-call rather than against one cached "current" sample list so it stays
 * correct even if a session somehow mixes versions from different looks.
 */
function resolveStyleSample(looks: Look[] | undefined, flatSamples: StyleSample[], styleId: string): StyleSample | undefined {
  if (looks && looks.length > 0) {
    const parsed = splitLookStyleId(styleId);
    if (!parsed) return undefined;
    const look = looks.find((l) => l.id === parsed.lookId);
    return look?.style_samples.find((s) => s.id === parsed.comboId);
  }
  return flatSamples.find((s) => s.id === styleId);
}

/**
 * PreviewApp — the standalone, shareable preview page (#bf-preview-root on
 * /pages/preview?id=<jobId>). Rebuilds the session from the URL's job_id via
 * GET /preview/{id}; variants + styles come from the Liquid-embedded products
 * map. Layout mirrors beyondframesstudio.com: Recent Portraits on top, the
 * watermarked image (with a Selected-for-Order / Retry bar) beside the
 * "Choose Your Format" card. Each format CTA opens the email gate → checkout.
 */
export function PreviewApp({ data }: Props) {
  const currency = window.__BF_CONFIG__?.currency || 'USD';
  const watermarkUrl = getWatermarkUrl();

  const [activeJobId, setActiveJobId] = useState<string | null>(() => getJobIdFromUrl());
  const [preview, setPreview] = useState<PreviewApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const { state: genState, clone } = useGenerateFlow();
  const handledCloneJobId = useRef<string | null>(null);
  const retryStyleIdRef = useRef<string | null>(null);
  const [showRetry, setShowRetry] = useState(false);

  const [orderingVariant, setOrderingVariant] = useState<ShopifyVariant | null>(null);
  const [checkoutPhase, setCheckoutPhase] = useState<CheckoutPhase>('idle');
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const fetchPreview = useCallback(async (jobId: string) => {
    setLoading(true);
    setLoadError(null);
    try {
      setPreview(await getPreview(jobId));
    } catch (err) {
      setLoadError(
        err instanceof ApiError && err.status === 404
          ? "We couldn't find this portrait. The link may have expired."
          : 'Could not load this portrait. Please refresh and try again.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!activeJobId) {
      setLoading(false);
      setLoadError('No portrait selected.');
      return;
    }
    fetchPreview(activeJobId);
  }, [activeJobId, fetchPreview]);

  const product = useMemo(
    () => (preview ? data.products[preview.product_id] ?? null : null),
    [preview, data.products],
  );
  const looks = product?.uiConfig.looks;
  const hasLooks = (looks?.length ?? 0) > 0;
  const flatStyleSamples = product?.uiConfig.style_samples ?? [];
  const variants = product?.variants ?? [];
  const moments = product?.uiConfig.moments ?? [];

  // 2-up example gallery for the processing screen — from the style being
  // retried (or the active style), with comma-joined URLs split out + topped up.
  const cleanImgs = (arr?: string[]) =>
    (arr ?? [])
      .flatMap((g) => (typeof g === 'string' ? g.split(',') : []))
      .map((s) => s.trim())
      .filter((g) => g.length > 0 && !g.includes('<<'));

  const versions = useMemo<Version[]>(() => {
    if (!preview) return [];
    return preview.versions
      .filter((v) => v.status === 'complete' && v.preview_url)
      .map((v) => {
        const sample = resolveStyleSample(looks, flatStyleSamples, v.style_id ?? '');
        return {
          jobId: v.job_id,
          previewUrl: v.preview_url as string,
          finalUrl: (v.final_url ?? v.preview_url) as string,
          styleId: v.style_id ?? '',
          styleName: sample?.name ?? sample?.badge,
          createdAt: v.created_at ?? '',
        };
      });
  }, [preview, looks, flatStyleSamples]);

  const activeVersion = useMemo<Version | null>(() => {
    if (versions.length === 0) return null;
    return versions.find((v) => v.jobId === activeJobId) ?? versions[versions.length - 1];
  }, [versions, activeJobId]);

  // Merged product: which look the active version was generated under, and
  // that look's own combo list (what RetryPanel's "Change style" picker
  // should offer — retry stays within the same look, never switches looks).
  const activeLookId = hasLooks ? (activeVersion ? splitLookStyleId(activeVersion.styleId)?.lookId ?? null : null) : null;
  const activeLook = hasLooks ? looks?.find((l) => l.id === activeLookId) ?? null : null;
  const styleSamples = hasLooks ? (activeLook?.style_samples ?? []) : flatStyleSamples;

  // Recent Portraits strip = every portrait this browser created (localStorage)
  // merged with the current session's versions, newest first. Re-reads
  // localStorage whenever the session versions change (e.g. after a retry).
  const stripItems = useMemo<{ jobId: string; previewUrl: string; styleName?: string }[]>(() => {
    const byId = new Map<string, { jobId: string; previewUrl: string; styleName?: string; createdAt: string }>();
    for (const v of versions) {
      byId.set(v.jobId, { jobId: v.jobId, previewUrl: v.previewUrl, styleName: v.styleName, createdAt: v.createdAt });
    }
    for (const r of getRecentPortraits()) {
      if (!byId.has(r.jobId)) {
        byId.set(r.jobId, { jobId: r.jobId, previewUrl: r.previewUrl, styleName: r.styleName, createdAt: r.createdAt });
      }
    }
    return [...byId.values()].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
  }, [versions]);

  const activeRaw = preview?.versions.find((v) => v.job_id === activeJobId) ?? null;
  const activePortraitMode = activeRaw?.portrait_mode ?? '';
  const stillProcessing = !!activeRaw && activeRaw.status === 'processing';

  // familypet-caption-45/-50 (SP7-10): photo count is fixed by the original
  // upload, so retry must lock the person/pet combo and only offer Clothing
  // Preference. portraitMode for these 2 modes is literally the same string
  // as their input_set_code, so getInputConfig() resolves correctly here.
  const dynamicPersonPetGroups =
    activePortraitMode === 'familypet-caption-45' || activePortraitMode === 'familypet-caption-50';
  // Merged product: the active look may itself hide Clothing Preference
  // (e.g. Timeless Monochrome has no clothing-variant prompts) — same
  // override App.tsx applies for the initial generate flow.
  const retryClothingConfig = dynamicPersonPetGroups && !activeLook?.hide_clothing_preference
    ? getInputConfig(activePortraitMode as InputSetCode)
    : null;

  useEffect(() => {
    if (!stillProcessing || !activeJobId) return;
    const t = window.setTimeout(() => fetchPreview(activeJobId), 3000);
    return () => clearTimeout(t);
  }, [stillProcessing, activeJobId, fetchPreview, preview]);

  const retryCount = Math.max(0, versions.length - 1);
  const generating = genState.status === 'uploading' || genState.status === 'generating';

  // Keep the phone screen awake while a retry is generating (~80s).
  useWakeLock(generating);

  // Examples of the style being retried (or the active one) for the processing
  // screen. retryStyleIdRef holds a plain combo id (RetryPanel only ever
  // offers the active look's own combos), so it matches styleSamples
  // directly; activeVersion.styleId may be the full "{look}__{combo}"
  // compound, which needs resolveStyleSample to parse.
  const genStyle = retryStyleIdRef.current
    ? styleSamples.find((s) => s.id === retryStyleIdRef.current) ?? null
    : (activeVersion ? resolveStyleSample(looks, flatStyleSamples, activeVersion.styleId) ?? null : null);
  let genPool = cleanImgs(genStyle?.gallery);
  if (genPool.length < 2) {
    genPool = genPool
      .concat(cleanImgs(genStyle?.manaquin_gallery))
      .concat(styleSamples.flatMap((s) => cleanImgs(s.gallery)));
    genPool = Array.from(new Set(genPool));
  }
  const genGallery = genPool.slice(0, 2);

  const goToVersion = useCallback((jobId: string) => {
    window.history.pushState({}, '', `?id=${encodeURIComponent(jobId)}`);
    setActiveJobId(jobId);
  }, []);

  const handleRetry = (newStyleId: string, clothing?: string) => {
    if (!activeVersion || retryCount >= MAX_RETRIES || generating) return;
    setShowRetry(false);
    // newStyleId is a plain combo id — RetryPanel only offers the active
    // look's own combos (styleSamples below), never a different look. Send
    // the backend the same "{look}__{combo}" compound the original job used.
    retryStyleIdRef.current = newStyleId;
    const sample = styleSamples.find((s) => s.id === newStyleId);
    const sendStyleId = hasLooks && activeLookId ? `${activeLookId}__${newStyleId}` : newStyleId;
    clone({
      sourceJobId: activeVersion.jobId,
      styleId: sendStyleId,
      portraitMode: '',
      styleName: sample?.name ?? sample?.badge,
      clothing,
    });
  };

  // When a retry completes, swap the URL to the new version + refetch session,
  // and record it in the browser's "Recent Portraits" history so each newly
  // generated version accumulates there (same as the first landing generate).
  useEffect(() => {
    if (genState.status !== 'done' || !genState.jobId) return;
    if (handledCloneJobId.current === genState.jobId) return;
    const jobId = genState.jobId;
    handledCloneJobId.current = jobId;

    const styleId = retryStyleIdRef.current;
    const sample = styleId ? styleSamples.find((s) => s.id === styleId) : undefined;

    let cancelled = false;
    void (async () => {
      // The CLI worker can't burn in the overlay text (its minimal PHP lacks
      // GD), so re-apply it here in the web process. The bridge reads the text
      // inherited onto the job — non-fatal, falls back to the clean render.
      let previewUrl = genState.result?.previewUrl ?? '';
      try {
        const ov = await reapplyOverlay(jobId);
        if (ov && typeof ov.url === 'string' && ov.url) previewUrl = ov.url;
      } catch {
        /* keep the clean render */
      }
      if (cancelled) return;

      saveRecentPortrait({
        jobId,
        previewUrl,
        styleName: sample?.name ?? sample?.badge,
        createdAt: new Date().toISOString(),
      });
      goToVersion(jobId);
    })();

    return () => { cancelled = true; };
  }, [genState.status, genState.jobId, genState.result, styleSamples, goToVersion]);

  useEffect(() => {
    const onPop = () => setActiveJobId(getJobIdFromUrl());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const handleOrder = (variant: ShopifyVariant) => {
    if (!activeVersion || !preview) return;
    // Re-entry guard: ignore extra clicks while a checkout is already in flight.
    // Double-clicks fire /cart/clear + /cart/add twice → Shopify 429s the burst.
    if (checkoutPhase !== 'idle') return;
    setOrderingVariant(variant);
    setCheckoutError(null);

    // NOTE: do NOT fire fbq('AddToCart') here. Shopify's native Meta pixel
    // already emits AddToCart (with a `sh-` eventID + server-side CAPI dedup)
    // when the item hits the cart below. A manual fbq call has no eventID, so
    // Facebook can't dedupe it → the event shows up twice. Same for
    // InitiateCheckout (handled by Shopify on checkout_started).
    void proceedToCheckout(variant);
  };

  // Add the chosen variant to the cart (with the AI image + style properties)
  // and redirect straight to checkout. No email step.
  const proceedToCheckout = async (variant: ShopifyVariant) => {
    if (!activeVersion || !preview) return;
    setCheckoutPhase('submitting');
    setCheckoutError(null);
    try {
      await addToCart([
        {
          variantId: variant.id,
          properties: {
            _session_id: activeVersion.jobId,
            _ai_image_url: activeVersion.previewUrl,
            _ai_image_url_highres: activeVersion.finalUrl,
            _style: activeVersion.styleId,
            _portrait_mode: activePortraitMode,
          },
        },
      ]);

      // InitiateCheckout is emitted by Shopify's native Meta pixel on
      // checkout_started (with eventID + CAPI dedup) — don't double-fire it.
      setCheckoutPhase('redirecting');
      goToCheckout();
    } catch (err) {
      const msg =
        err instanceof CartError
          ? err.shopifyDescription || err.message
          : err instanceof Error
            ? err.message
            : 'Could not start checkout. Please try again.';
      setCheckoutError(msg);
      setCheckoutPhase('idle');
    }
  };

  // ─── Render states ──────────────────────────────────────────────────────

  if (loading && !preview) return <CenteredNote>Loading your portrait…</CenteredNote>;
  if (loadError) return <CenteredNote tone="error">{loadError}</CenteredNote>;
  if (preview && !product) {
    return <CenteredNote tone="error">This portrait's product isn't configured on the preview page.</CenteredNote>;
  }
  if (stillProcessing && !activeVersion) {
    return <CenteredNote>Your portrait is still being painted… hang tight.</CenteredNote>;
  }
  if (!activeVersion) return <CenteredNote tone="error">No portrait is ready for this link yet.</CenteredNote>;

  return (
    <div className="w-full max-w-[940px] mx-auto px-4 sm:px-6 py-6 flex flex-col gap-6">
      <StepBar active={3} />

      {/* Retry / generation error notice (e.g. daily-limit reached). */}
      {genState.status === 'failed' && (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-[14px] border p-4"
          style={{ background: 'rgba(245,158,11,0.10)', borderColor: 'rgba(245,158,11,0.35)' }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0" aria-hidden="true" style={{ marginTop: 1 }}>
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <p className="text-sm m-0 leading-relaxed" style={{ color: '#7a4a12' }}>
            {genState.error || 'Could not create that version. Please try again.'}
          </p>
        </div>
      )}

      {/* Recent Portraits */}
      <section className="flex flex-col gap-3">
        <h2 className="flex items-center gap-2 m-0 text-warm-brown font-bold text-base">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="9" cy="9" r="2" />
            <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
          </svg>
          Recent Portraits
        </h2>
        <div className="flex gap-3 overflow-x-auto pb-1">
          {stripItems.map((v) => {
            const isActive = v.jobId === activeJobId;
            return (
              <button
                key={v.jobId}
                type="button"
                onClick={() => goToVersion(v.jobId)}
                aria-current={isActive ? 'true' : undefined}
                aria-label={v.styleName ?? 'Portrait'}
                className={[
                  'relative flex-shrink-0 w-[88px] aspect-[3/4] rounded-[12px] overflow-hidden border-2 transition-all',
                  isActive ? 'border-teal bf-glow' : 'border-soft-border opacity-80 hover:opacity-100',
                ].join(' ')}
              >
                <RetryImage src={shopifyImg(v.previewUrl, 300)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                <img
                  src={watermarkUrl}
                  alt=""
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 w-full h-full object-cover opacity-[0.85]"
                />
                {isActive && (
                  <span
                    aria-hidden="true"
                    className="absolute bottom-1.5 right-1.5 rounded-full bg-teal text-white shadow"
                    style={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <svg width="11" height="11" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path d="M3.5 8.5l3 3 6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </section>

      {/* While a retry is generating, show the same processing screen as the
          product page: progress bar + 2-up style gallery + moments. */}
      {generating && (
        <section className="py-8 flex flex-col gap-10 items-center">
          <div className="flex flex-col gap-6 items-center">
            <h2 className="font-sans text-xl font-bold text-warm-brown text-center m-0">
              Painting your new version…
            </h2>
            <ProgressBar done={false} />
          </div>

          {genGallery.length > 0 && (
            <div
              className="w-full"
              style={{ maxWidth: 520, display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 12 }}
            >
              {genGallery.map((src, i) => (
                <div
                  key={i}
                  className="rounded-[14px] overflow-hidden bg-cream2 border border-soft-border shadow-soft"
                  style={{ aspectRatio: '3 / 4' }}
                >
                  <img src={shopifyImg(src, 500)} alt="" loading="lazy" decoding="async" className="w-full h-full" style={{ objectFit: 'cover' }} />
                </div>
              ))}
            </div>
          )}

          <MomentsSection moments={moments} />
        </section>
      )}

      {/* Image 58% + format 42% (sticky) — stacks to 1 col under 768px.
          Layout via raw CSS (bf-preview-*) not Tailwind responsive utilities. */}
      {!generating && (
      <>
      <div className="bf-preview-layout">
        <div className="bf-preview-image-col">
          {/* One rounded card: portrait on top, the Selected-for-Order + Retry
              bar (cream→gold gradient) below — matches woo bf-image-wrap. */}
          <div className="rounded-[16px] overflow-hidden border border-soft-border shadow-soft">
            <PreviewImage active={activeVersion} />

            <div
              className="flex items-center justify-between gap-3 px-[18px] py-[13px] border-t"
              style={{
                background: 'linear-gradient(135deg, #ede5d5 0%, #f0e4cc 100%)',
                borderTopColor: 'rgba(168,118,62,0.3)',
              }}
            >
              <span className="inline-flex items-center gap-2 text-[13px] font-medium text-teal-dark">
                <span aria-hidden="true" className="bf-live-dot w-[9px] h-[9px] rounded-full bg-[#3ecf8e]" />
                Selected for Order
              </span>
              <button
                type="button"
                onClick={() => setShowRetry(true)}
                disabled={generating || retryCount >= MAX_RETRIES}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-teal text-white text-xs font-semibold uppercase tracking-[0.08em] shadow-soft hover:bg-teal-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                  <path d="M3 3v5h5" />
                </svg>
                Retry
              </button>
            </div>
          </div>
        </div>

        <div className="bf-preview-format-col flex flex-col gap-4">
          <FormatAccordion
            variants={variants}
            currency={currency}
            onOrder={handleOrder}
            busy={checkoutPhase === 'submitting' || checkoutPhase === 'redirecting'}
            busyVariantId={orderingVariant?.id ?? null}
          />
          {checkoutError && (
            <p className="text-sm text-red-600 text-center">{checkoutError}</p>
          )}
        </div>
      </div>

      {/* Per-product reviews from ui_config.moments */}
      <CustomerReviews moments={moments} />

      {/* Static global support + FAQ (hardcoded from template-woo) */}
      <SupportFaq />
      </>
      )}

      {/* Retry modal */}
      {showRetry && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/55"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowRetry(false);
          }}
        >
          <div className="relative w-full" style={{ maxWidth: 520 }}>
            <button
              type="button"
              onClick={() => setShowRetry(false)}
              aria-label="Close"
              className="absolute -top-3 -right-3 z-10 rounded-full bg-white text-warm-brown shadow-soft border border-soft-border hover:text-black"
              style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, lineHeight: 0 }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
            <RetryPanel
              samples={styleSamples}
              currentStyleId={hasLooks ? (splitLookStyleId(activeVersion.styleId)?.comboId ?? activeVersion.styleId) : activeVersion.styleId}
              retryCount={retryCount}
              disabled={generating}
              onRetry={handleRetry}
              onUploadNew={() => window.location.assign(product ? `/products/${product.handle}` : '/')}
              lockCombo={dynamicPersonPetGroups}
              clothingOptions={retryClothingConfig?.clothingOptions}
              clothingLabel={retryClothingConfig?.clothingLabel}
              clothingDefaultId={retryClothingConfig?.clothingDefaultId}
            />
          </div>
        </div>
      )}

      <JobMonitor />
    </div>
  );
}

function CenteredNote({ children, tone }: { children: React.ReactNode; tone?: 'error' }) {
  return (
    <div className="w-full max-w-2xl mx-auto px-4 py-16 text-center">
      <p className={tone === 'error' ? 'text-warm-brown' : 'text-warm-brown/70'}>{children}</p>
      <JobMonitor />
    </div>
  );
}
